import { useEffect, useRef, useState } from 'react'
import { GOOGLE_CLIENT_ID, googleAuth, setToken, type User } from '@/api/auth'

// Google Sign-In via Google Identity Services. Renders the real Google button when
// VITE_GOOGLE_CLIENT_ID is configured; otherwise a friendly disabled state.
export function SocialButtons({ onAuth }: { onAuth: (u: User) => void }) {
  const googleDiv = useRef<HTMLDivElement>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return
    const id = 'gis-script'
    const init = () => {
      const g = (window as any).google
      if (!g?.accounts?.id || !googleDiv.current) return
      g.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (resp: { credential: string }) => {
          const { ok, data } = await googleAuth(resp.credential)
          if (ok && data.token) { setToken(data.token); onAuth(data.user) }
          else setErr(data.detail || 'Google sign-in failed.')
        },
      })
      g.accounts.id.renderButton(googleDiv.current, { theme: 'outline', size: 'large', width: 320, text: 'continue_with' })
    }
    if (document.getElementById(id)) { init(); return }
    const s = document.createElement('script')
    s.id = id; s.src = 'https://accounts.google.com/gsi/client'; s.async = true; s.onload = init
    document.head.appendChild(s)
  }, [onAuth])

  if (!GOOGLE_CLIENT_ID) {
    return (
      <div className="space-y-2">
        <DisabledSocial label="Continue with Google" icon={<GoogleIcon />} />
        <DisabledSocial label="Continue with Apple" icon={<span className="text-base"></span>} />
        <p className="text-center text-[10px] text-muted-foreground">Social sign-in activates once OAuth is configured.</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div ref={googleDiv} className="flex justify-center" />
      <DisabledSocial label="Continue with Apple" icon={<span className="text-base"></span>} />
      {err && <p className="text-center text-xs text-destructive-text">{err}</p>}
    </div>
  )
}

function DisabledSocial({ label, icon }: { label: string; icon: React.ReactNode }) {
  return (
    <button
      disabled
      className="flex w-full cursor-not-allowed items-center justify-center gap-2.5 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-muted-foreground opacity-70"
    >
      {icon} {label}
    </button>
  )
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.8-6.8C35.6 2.4 30.1 0 24 0 14.6 0 6.5 5.4 2.5 13.2l7.9 6.2C12.3 13.3 17.6 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9.1h12.4c-.5 2.9-2.1 5.3-4.6 7l7.1 5.5c4.2-3.9 6.6-9.6 6.6-16z" />
      <path fill="#FBBC05" d="M10.4 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.2C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.8l7.9-6.2z" />
      <path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.5l-7.1-5.5c-2 1.4-4.6 2.2-7.9 2.2-6.4 0-11.7-3.8-13.6-9.4l-7.9 6.2C6.5 42.6 14.6 48 24 48z" />
    </svg>
  )
}
